import { z } from 'zod';
import type { Readiness } from './catalog.js';

export const SHEET_TYPES = [
  'overview',
  'step_detail',
  'scope_register',
  'scenario_flow',
  'freeform',
] as const;
export type SheetType = (typeof SHEET_TYPES)[number];

// Ziel-Felder je Blatt-Typ. 'ignore' = Design-Spec 3.8 "nicht importieren ▾".
// Meiste Felder entsprechen direkt Katalog-Spalten aus Plan 2. Vier Felder sind Lookups
// (Namen, kein _key-Suffix), die build.ts (Task 12) zu Objekten auflöst:
// — process_name_de, process_name_en: Prozess-Objekt nach Name auflösen
// — e2e_name_de: E2E-Kette nach Name auflösen
// — fachbereich: Teilprojekt-Knoten nach Name auflösen (#112, A10)
// Nicht importiert wird die Spalte "Prozessgruppe": die Zwischenebene wurde mit `modul`
// abgeschafft, die Suche über source_ref (A6) ersetzt sie.
// test_case_human_id ist die fachliche Nummer aus der Quelldatei — NICHT die vom
// kundenweiten Zähler vergebene menschenlesbare ID (die kommt erst beim Commit).
export const IMPORT_TARGET_FIELDS = {
  overview: [
    'test_case_human_id',
    'title_de',
    'title_en',
    'process_name_de',
    'process_name_en',
    'precondition_de',
    'precondition_en',
    'expected_de',
    'expected_en',
    'polarity',
    'readiness',
    'ignore',
  ],
  step_detail: [
    'test_case_human_id',
    // Der Titel identifiziert den Testfall, zu dem die Schritte gehoeren — in der Sprache, in der
    // die Quelldatei ihn fuehrt. Eine englische Datei braucht dafuer title_en, sonst muesste
    // englischer Text auf das DE-Feld gemappt werden (build.ts kannte beide Seiten schon).
    'title_de',
    'title_en',
    'step_no',
    'description_de',
    'description_en',
    'tcode',
    'expected_output_de',
    'expected_output_en',
    'notes',
    'e2e_name_de',
    'e2e_name_en',
    'input',
    // #141: „Responsible Person" → Standard-Tester des Testfalls, gleiche Bedeutung wie bei
    // scenario_flow. build.ts nimmt das erste Kürzel der ersten Schrittzeile mit Wert.
    'responsible_code',
    'ignore',
  ],
  scope_register: [
    // #112 (A6): die fachliche Prozess-ID der Quelldatei ("WMI_09", Spalte "Prozess-ID") — sie
    // wird als processes.source_ref gespeichert und ist die Bruecke zurueck in die Importdatei.
    // Bewusst NICHT sap_scope_item_id: dort stehen SAP-Scope-Items wie "BD9".
    'process_human_id',
    'process_name_de',
    'process_name_en',
    'sap_scope_item_id',
    'in_scope',
    // #112 (A10): der Fachbereich der Prozesszeile ("Vertrieb (SAP SD)") — erste Quelle der
    // Teilprojekt-Rangfolge im Import, kann je Zeile variieren. Loest 'stream_name' ab: ein
    // Prozess haengt nie mehr an einem Stream, deshalb gibt es hier auch kein Stream-Ziel mehr.
    'fachbereich',
    'ignore',
  ],
  scenario_flow: [
    'scenario_id',
    'scenario_name',
    'test_case_title',
    'tcode',
    'input',
    'expected',
    'polarity',
    'readiness',
    'responsible_code',
    // Die Zeile erzeugt genau einen Schritt — dessen Notizfeld war hier als einziges Blatt nicht
    // erreichbar, obwohl step_detail es laengst anbietet.
    'notes',
    'ignore',
  ],
  freeform: ['ignore'],
} as const satisfies Record<SheetType, readonly string[]>;

export function isTargetField(sheetType: SheetType, field: string): boolean {
  return (IMPORT_TARGET_FIELDS[sheetType] as readonly string[]).includes(field);
}

// --- LLM-Antwort-Schemas (Layer-1-Ausgangsvalidierung, siehe llm/client.ts) ---
export const SheetClassificationSchema = z.object({
  sheet_type: z.enum(SHEET_TYPES),
  confidence: z.number().min(0).max(1),
  reason: z.string().max(300),
  source_language: z.enum(['de', 'en']).optional(),
});
export type SheetClassification = z.infer<typeof SheetClassificationSchema>;

export const ColumnMappingProposalSchema = z.object({
  mappings: z.array(
    z.object({
      column: z.string(),
      target_field: z.string(),
      confidence: z.number().min(0).max(1),
    }),
  ),
});
export type ColumnMappingProposal = z.infer<typeof ColumnMappingProposalSchema>;

export const TranslationBatchSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      // #63/#64: bidirektional — der übersetzte Text der jeweils in der Anfrage genannten
      // `target_lang` (nicht mehr fix Englisch).
      text: z.string(),
      confidence: z.number().min(0).max(1),
    }),
  ),
});
export type TranslationBatch = z.infer<typeof TranslationBatchSchema>;

// --- Wizard-DTOs ---
// #67 (Task 8): `sheet_type` UND `excluded` sind beide optional, weil die Blatt-Ignorieren-Aktion
// (UI-Checkbox, Task 11) unabhängig vom Klassifikations-Schritt sein muss — ein Mensch markiert
// ein Deckblatt/eine Legende als "ignorieren", ohne dabei den Blatt-Typ neu zu bestätigen. Beide
// Felder leer wäre ein No-Op-PATCH; das erlaubt die Route (kein Zwang, mindestens eins zu senden).
// #72: Quellsprache ist wie der Blatt-Typ nur ein Vorschlag (KI/Heuristik) — der Mensch muss sie
// korrigieren können, sonst läuft eine Fehl-Erkennung unbemerkt in die falsche Übersetzungsrichtung
// (der #63-Schaden, nur diesmal blattweit statt feldweise). Optional wie sheet_type/excluded: ein
// reiner Sprach-Umschalter darf keinen neuen Mapping-Vorschlag anstoßen (siehe Route unten).
export const SheetTypeConfirmSchema = z.object({
  sheet_type: z.enum(SHEET_TYPES).optional(),
  excluded: z.boolean().optional(),
  source_language: z.enum(['de', 'en']).optional(),
});
export const MappingConfirmSchema = z.object({ mappings: z.record(z.string(), z.string()) });

// Task 8: Stream/Teilprojekt der Struktur-Zielfelder (stream_name, scope_register-Zeile 46) —
// beide optional/partiell, damit ein onBlur je Feld unabhängig speichert, ohne das jeweils andere
// Feld zu überschreiben. `teilprojekt_name: null` löscht das Feld bewusst (optionales Feld).
export const StructureUpdateSchema = z.object({
  stream_name: z.string().min(1).optional(),
  teilprojekt_name: z.string().nullable().optional(),
  // #125 (Q5): „Neuimport von" — null = Neuanlage wie bisher.
  reimport_of_job_id: z.string().uuid().nullable().optional(),
  // #125 (Fix-Runde 2): Struktur-Felder, die der Admin im Upload-Schritt selbst bearbeitet hat. Nur
  // mit `reimport_of_job_id` sinnvoll: diese Felder übernimmt ein Quellwechsel nie. Nicht gespeichert —
  // der Wizard kennt den Stand für die Dauer des Schritts.
  edited_fields: z.array(z.enum(['stream_name', 'teilprojekt_name'])).optional(),
});

// #125: 'reimport' = aus dem gleichnamigen Blatt des Quell-Imports übernommen (Vorbelegung beim
// Neuimport) — wie 'profile' bestätigtes Menschenwissen, hat aber Vorrang vor dem Profil.
// #151: dieselbe Quelle trägt auch das eigene, schon gespeicherte Mapping dieses Blatts, wenn der
// Mapping-Schritt erneut geöffnet wird (Rücksprung aus der Vorschau).
export type MappingSource = 'profile' | 'reimport' | 'ai' | 'none';

// #125: Vorbelegung eines Blatts aus dem Quell-Import (abgeleitet, nicht gespeichert).
// 'applied' = Typ, Sprache, Ignorieren und Mapping entsprechen (noch) dem gleichnamigen Blatt der
// Quelle; 'from_other_import' = die Werte stammen aus einem ANDEREN Import (frühere Quelle; die
// gewählte hat das Blatt nicht, führt andere Spalten, oder es ist keine gewählt) → source_* nennt
// diesen Import; 'kept_without_source' = dasselbe, aber es ist gerade keine Quelle gewählt (nach
// „keiner"); 'headers_differ' = gleichnamiges Blatt in der Quelle, aber andere Spalten → nichts
// übernommen, der KI-Vorschlag bleibt und der Admin soll ihn prüfen.
export interface SheetPrefill {
  status: 'applied' | 'from_other_import' | 'kept_without_source' | 'headers_differ';
  source_job_id: string;
  source_filename: string;
}

// #125 (Folgeauftrag): Stream/Teilprojekt aus dem neuesten Job der Quell-Familie übernommen —
// je Feld true, solange es (noch) den Wert der Quelle trägt (abgeleitet, nicht gespeichert).
export interface StructurePrefill {
  source_job_id: string;
  source_filename: string;
  stream_name: boolean;
  teilprojekt_name: boolean;
}
export interface MappingProposalEntry {
  column: string;
  target_field: string; // 'ignore', wenn nichts vorgeschlagen wird
  confidence: number;
  source: MappingSource;
  // IMP-3 (Task-19-Review, Fund 1, PO-Entscheidung 2026-07-17): erste GÜLTIGE Zeile (keine als
  // Müll verworfene) mit einem nicht-leeren Wert für diese Spalte — damit der Mensch einen
  // KI-Vorschlag überhaupt beurteilen kann. `null`, wenn die Spalte in keiner gültigen Zeile
  // einen Wert trägt.
  sample: string | null;
}

export interface DuplicateHint {
  kind: 'test_case' | 'process';
  label: string; // menschenlesbar: Quell-ID oder Titel
  // #125: 'source_ref_collision' ersetzt 'human_id_collision' — verglichen wird die Quell-ID der
  // Datei mit test_cases.source_ref (vorher fälschlich mit human_id).
  reason: 'source_ref_collision' | 'title_collision';
  against: 'catalog' | 'import'; // bestehender Katalog oder innerhalb dieses Imports
}

// #110: Felder, die die KI-Übersetzung im Import NICHT erreicht hat (Gateway-Ausfall, Ratenlimit,
// Budget). Ohne diese Zusammenfassung sähe die Vorschau wie ein vollständiger Import aus.
export interface UntranslatedSummary {
  fields: number;
  // Anzahl ENTITÄTEN mit mindestens einem offenen Feld (ein Schritt zählt einzeln).
  by_kind: { test_case: number; step: number; process: number; e2e: number };
  reason: string; // deutscher Text samt Hinweis, wie die Lücke im Katalog nachgeholt wird
}

export interface ImportPreview {
  processes: number;
  test_cases: number;
  steps: number;
  e2e: number;
  translations: number; // Anzahl KI-übersetzter Felder (LNG-3)
  review_flags: number; // davon unter der Konfidenzschwelle (LNG-4)
  discarded_rows: number; // IMP-5
  duplicates: DuplicateHint[]; // IMP-6
  warnings: string[];
  // #110: null = nichts blieb unübersetzt (oder es gab nichts zu übersetzen).
  untranslated: UntranslatedSummary | null;
  // #125: nur bei Neuimport (import_jobs.reimport_of_job_id gesetzt) — der Abgleich gegen den
  // Katalog. null = Neuanlage wie bisher.
  reimport: ReimportComparison | null;
}

// #73: Was am Import-Vorgang gespeichert wird — ohne die Teile, die beim Lesen frisch aus dem
// Katalog berechnet werden (Dubletten, Neuimport-Abgleich), weil sie sonst veralten würden.
export type StoredImportPreview = Omit<ImportPreview, 'duplicates' | 'reimport'>;

// #73: Berechnungszustand eines Import-Vorgangs, getrennt vom Importstatus (upload/classified/…).
// 'interrupted' = Neustart des Servers mitten im Lauf (UI: „abgebrochen"), neu startbar.
export const IMPORT_COMPUTATION_KINDS = ['classification', 'preview'] as const;
export type ImportComputationKind = (typeof IMPORT_COMPUTATION_KINDS)[number];
export const IMPORT_COMPUTATION_STATES = ['running', 'done', 'failed', 'interrupted'] as const;
export type ImportComputationState = (typeof IMPORT_COMPUTATION_STATES)[number];

export const ImportComputationSchema = z.object({
  kind: z.enum(IMPORT_COMPUTATION_KINDS).nullable(),
  state: z.enum(IMPORT_COMPUTATION_STATES).nullable(),
  done: z.number().int().min(0),
  total: z.number().int().min(0),
  error: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});
export type ImportComputation = z.infer<typeof ImportComputationSchema>;

// #125 (Q5): ein früherer, committeter Import als mögliche Quelle eines Neuimports.
export interface ReimportCandidate {
  import_job_id: string;
  filename: string;
  committed_at: string; // ISO
  test_cases: number; // Testfälle, deren Quell-Import dieser Job heute ist
  // Import-Familie (Wurzel + Neuimporte über reimport_of_job_id) = ID ihres ältesten Jobs. Jede Wahl
  // aus derselben Familie gleicht gegen denselben Bestand ab; ein älterer Job mit 0 Testfällen ist
  // von einem späteren Neuimport abgelöst — die Auswahl gruppiert und erklärt das (#125).
  family_id: string;
}

// --- Nutzer-Zuordnung (Import-Nutzerzuordnung #65) ---
export interface UserMatchCandidate {
  user_id: string;
  name: string;
  email: string;
  score: number; // 0..1, höher = ähnlicher
}
export interface UserCodeEntry {
  code: string;
  guessed_name: string;
  matches: UserMatchCandidate[];
  mapped_user_id: string | null; // Vorbelegung aus import_user_map
}

// #75 Teil 1: die volle (projekt-/kundenweite) Kandidatenliste für die Freitextsuche im
// Nutzer-Zuordnungsschritt — Ergänzung zu den fuzzy Top-5 aus UserCodeEntry.matches, für den Fall,
// dass der richtige Nutzer dort nicht auftaucht. Kommt in derselben Antwort wie `codes` (GET
// .../user-codes), weil der Handler `allUsers` ohnehin schon lädt (kein Zusatz-Query) — nur die
// aktiven Nutzer, ein deaktiviertes Konto soll man hier nicht mehr zuordnen können.
export interface UserDirectoryEntry {
  user_id: string;
  name: string;
  email: string;
}

export const UserMapConfirmSchema = z.object({
  assignments: z.array(
    z.object({
      code: z.string().min(1),
      action: z.enum(['assign', 'stub', 'ignore']),
      user_id: z.string().uuid().optional(),
      name: z.string().optional(),
      email: z.string().email().nullable().optional(),
    }),
  ),
});
export type UserMapAssignment = z.infer<typeof UserMapConfirmSchema>['assignments'][number];

// --- Bundle C (#125): Neuimport mit Abgleich ---
// Feld-Vokabular des Abgleichs. Polarität, Reifegrad und Plan-Zuweisungen fehlen ABSICHTLICH:
// sie gehören TestFlow (Q6) und werden vom Neuimport nie angefasst — außer dem Reifegrad über die
// ausdrückliche entfällt-Aktion.
export const REIMPORT_FIELDS = [
  'title',
  'precondition',
  'expected',
  'process',
  'default_tester',
  'steps',
] as const;
export type ReimportField = (typeof REIMPORT_FIELDS)[number];
export const REIMPORT_CHAIN_FIELDS = ['name', 'members'] as const;
export type ReimportChainField = (typeof REIMPORT_CHAIN_FIELDS)[number];
export type ReimportCategory = 'neu' | 'geaendert' | 'unveraendert' | 'entfaellt' | 'konflikt';
// Q4: Vorgabe nicht_im_scope (umkehrbar, Historie bleibt); löschen nur ohne Läufe (#106).
export const DROPPED_ACTIONS = ['nicht_im_scope', 'behalten', 'loeschen'] as const;
export type DroppedAction = (typeof DROPPED_ACTIONS)[number];

// 'collision': die Kettenglieder der Datei enthalten einen Testfall, dessen Abgleich-Schlüssel
// nicht eindeutig ist (siehe ReimportCollisionItem). Die Liste lässt sich dann nicht eindeutig
// schreiben — der Diff ist sichtbar, aber NICHT auf „Datei übernehmen" umschaltbar (kein Patch).
export type ReimportConflictReason = 'value' | 'translation' | 'collision';

// Ein Schritt in der Abgleich-Anzeige — dieselben Spalten, die der Abgleich vergleicht. Sprachpaare
// als „DE / EN" zusammengefasst wie in `tf`/`file`.
export interface ReimportStepView {
  description: string;
  expected: string;
  input: string;
  tcode: string;
  notes: string;
}

export interface ReimportFieldDiff<F extends string = ReimportField> {
  field: F;
  outcome: 'take_file' | 'conflict';
  conflict_reason: ReimportConflictReason | null;
  tf: string; // Anzeige: Wert in TestFlow
  file: string; // Anzeige: was „Datei übernehmen" schreibt
  // Nur bei field 'steps': dieselbe Schrittliste strukturiert. `tf`/`file` tragen die
  // Spaltenbezeichnungen als deutschen Text; die Oberfläche übersetzt sie aus dieser Struktur (#125).
  steps?: { tf: ReimportStepView[]; file: ReimportStepView[] };
}

// Ein Element mit einem Abgleich-Schlüssel, den auf Datei- ODER Katalogseite mehr als ein Element
// trägt (z. B. zwei kettenlose Testfälle gleichen Titels). Jede automatische Wahl hätte eines
// davon still aus dem Abgleich genommen oder das falsche überschrieben. Darum: alle beteiligten
// Elemente beider Seiten stehen in EINER Konfliktzeile, und der Neuimport tut mit ihnen nichts —
// nicht anlegen, nicht ändern, nicht „entfällt". Auflösen = Datei oder Katalog eindeutig machen.
export interface ReimportCollisionItem {
  side: 'file' | 'catalog';
  test_case_id: string | null; // null = Dateizeile
  human_id: string | null;
  source_ref: string | null;
  chain_ref: string | null;
  title: string;
  run_count: number;
}

export interface ReimportTestCaseRow {
  key: string; // Abgleich-Schlüssel (id:/kette:/titel:)
  category: ReimportCategory;
  test_case_id: string | null;
  human_id: string | null;
  source_ref: string | null;
  chain_ref: string | null;
  title: string;
  diffs: ReimportFieldDiff[];
  run_count: number; // Q1: „hat N Läufe"
  // entfällt-Aktion „löschen" erlaubt (#106): keine Läufe, kein Defect-Bezug (primär oder
  // „blockiert außerdem"), keine Ketten-Freigabe. Nur bei entfällt-Zeilen true; der Commit prüft
  // dasselbe noch einmal unter Zeilensperre (409 testcase_has_evidence).
  deletable: boolean;
  readiness: Readiness | null;
  deleted_in_testflow: boolean; // neu, aber in der Basis vorhanden → Vorgabe abgewählt
  collision: ReimportCollisionItem[]; // nicht leer = Schlüssel-Kollision, Kategorie konflikt, keine Aktion
}

export interface ReimportChainCollisionItem {
  side: 'file' | 'catalog';
  e2e_id: string | null; // null = Dateikette
  human_id: string | null;
  source_ref: string | null;
  name: string;
}

export interface ReimportChainRow {
  key: string;
  category: ReimportCategory;
  e2e_id: string | null;
  human_id: string | null;
  source_ref: string | null;
  name: string;
  diffs: ReimportFieldDiff<ReimportChainField>[];
  // Glieder laut Datei in Reihenfolge, als Abgleich-Schlüssel der Testfall-Zeilen ([] bei entfällt
  // und Kollision). Eine NEUE Kette wird nur angelegt, wenn jedes Glied eine Testfall-Zeile mit
  // test_case_id hat oder eine ausgewählte „neu"-Zeile ohne Kollision ist — sonst übersprungen
  // (CommitResult.chains_skipped_incomplete), damit die Kette beim nächsten Neuimport vollständig
  // als „neu" wiederkommt statt still mit Lücke (#125). Entsteht eine neue Kette nicht
  // (unvollständig, abgewählt, Konflikt), werden ihre neuen Glieder mit Schlüssel
  // `kette:<key>#…` ebenfalls nicht angelegt (CommitResult.test_cases_held_back) — bis zum Fixpunkt.
  member_keys: string[];
  collision: ReimportChainCollisionItem[]; // wie ReimportTestCaseRow.collision, für Ketten-Schlüssel
}

export interface ReimportError {
  code: 'duplicate_source_ref' | 'source_ref_taken';
  label: string;
  human_id: string | null;
}

export interface ReimportComparison {
  reimport_of_job_id: string;
  test_cases: ReimportTestCaseRow[];
  chains: ReimportChainRow[];
  errors: ReimportError[];
  // Fingerabdruck der Vorschau. Der Commit rechnet neu und verweigert bei Abweichung (409
  // reimport_preview_stale) — der Admin entscheidet nie über einen Stand, den er nicht gesehen hat.
  hash: string;
}

// Entscheidungen des Admins. Was fehlt, bekommt die Vorgabe: ausgewählt (außer „in TestFlow
// gelöscht"), Konflikte → TestFlow behalten, entfällt → nicht_im_scope.
export const ReimportDecisionsSchema = z.object({
  comparison_hash: z.string().min(1),
  test_cases: z
    .array(
      z.object({
        key: z.string().min(1),
        selected: z.boolean().optional(),
        take_file: z.array(z.enum(REIMPORT_FIELDS)).optional(),
        dropped_action: z.enum(DROPPED_ACTIONS).optional(),
      }),
    )
    .default([]),
  // Ketten ohne Datei-Gegenstück (entfällt) haben bewusst KEINE Aktion (kein dropped_action):
  // sie werden nur angezeigt und bleiben unangetastet (Entscheidung 03.10., #125).
  chains: z
    .array(
      z.object({
        key: z.string().min(1),
        selected: z.boolean().optional(),
        take_file: z.array(z.enum(REIMPORT_CHAIN_FIELDS)).optional(),
      }),
    )
    .default([]),
});
export type ReimportDecisions = z.infer<typeof ReimportDecisionsSchema>;
